// utils/rbac.ts - Role-Based Access Control for Dashboard Components

// STREAMLINED: 6 core roles (removed: approver, analyst, scheduler, misViewer)
export type UserRole = 'superadmin' | 'admin' | 'manager' | 'auditor' | 'editor' | 'user';

export interface DashboardPermissions {
  canViewOverview: boolean;
  canViewKpis: boolean;
  canViewAiInsights: boolean;
  canViewUnitComparison: boolean;
  canViewAdvancedAnalytics: boolean;
  canExportData: boolean;
  canManageApprovals: boolean;
  canViewAllUnits: boolean;
  canScheduleReports: boolean;
  canViewFinancialDetails: boolean;
}

export interface KpiVisibility {
  totalExpense: boolean;
  costPerUnit: boolean;
  approvalDelays: boolean;
  topCostCenters: boolean;
  budgetVariance: boolean;
  monthlyTrends: boolean;
  categoryBreakdown: boolean;
  pendingApprovals: boolean;
}

/**
 * Define dashboard visibility matrix for different roles
 * STREAMLINED: manager role combines approver + analyst + misViewer capabilities
 */
export const ROLE_PERMISSIONS: Record<UserRole, DashboardPermissions> = {
  superadmin: {
    canViewOverview: true,
    canViewKpis: true,
    canViewAiInsights: true,
    canViewUnitComparison: true,
    canViewAdvancedAnalytics: true,
    canExportData: true,
    canManageApprovals: true,
    canViewAllUnits: true,
    canScheduleReports: true,
    canViewFinancialDetails: true,
  },
  admin: {
    canViewOverview: true,
    canViewKpis: true,
    canViewAiInsights: true,
    canViewUnitComparison: true,
    canViewAdvancedAnalytics: true,
    canExportData: true,
    canManageApprovals: true,
    canViewAllUnits: true,
    canScheduleReports: true,
    canViewFinancialDetails: true,
  },
  manager: {
    canViewOverview: true,
    canViewKpis: true,
    canViewAiInsights: true,
    canViewUnitComparison: true,
    canViewAdvancedAnalytics: true,
    canExportData: true,
    canManageApprovals: true, // Merged from approver
    canViewAllUnits: false, // Only assigned units
    canScheduleReports: true,
    canViewFinancialDetails: true,
  },
  auditor: {
    canViewOverview: true,
    canViewKpis: true,
    canViewAiInsights: false,
    canViewUnitComparison: true,
    canViewAdvancedAnalytics: false,
    canExportData: true,
    canManageApprovals: false,
    canViewAllUnits: true, // Audit access
    canScheduleReports: false,
    canViewFinancialDetails: true,
  },
  editor: {
    canViewOverview: true,
    canViewKpis: true,
    canViewAiInsights: false,
    canViewUnitComparison: true,
    canViewAdvancedAnalytics: false,
    canExportData: false,
    canManageApprovals: false,
    canViewAllUnits: false,
    canScheduleReports: false,
    canViewFinancialDetails: false,
  },
  user: {
    canViewOverview: true,
    canViewKpis: false,
    canViewAiInsights: false,
    canViewUnitComparison: false,
    canViewAdvancedAnalytics: false,
    canExportData: false,
    canManageApprovals: false,
    canViewAllUnits: false,
    canScheduleReports: false,
    canViewFinancialDetails: false,
  },
};

/**
 * Define KPI visibility for different roles
 */
export const ROLE_KPI_VISIBILITY: Record<UserRole, KpiVisibility> = {
  superadmin: {
    totalExpense: true,
    costPerUnit: true,
    approvalDelays: true,
    topCostCenters: true,
    budgetVariance: true,
    monthlyTrends: true,
    categoryBreakdown: true,
    pendingApprovals: true,
  },
  admin: {
    totalExpense: true,
    costPerUnit: true,
    approvalDelays: true,
    topCostCenters: true,
    budgetVariance: true,
    monthlyTrends: true,
    categoryBreakdown: true,
    pendingApprovals: true,
  },
  manager: {
    totalExpense: true,
    costPerUnit: true,
    approvalDelays: true,
    topCostCenters: true,
    budgetVariance: true,
    monthlyTrends: true,
    categoryBreakdown: true,
    pendingApprovals: true, // Can manage approvals
  },
  auditor: {
    totalExpense: true,
    costPerUnit: true,
    approvalDelays: true,
    topCostCenters: true,
    budgetVariance: true,
    monthlyTrends: true,
    categoryBreakdown: true,
    pendingApprovals: false,
  },
  editor: {
    totalExpense: true,
    costPerUnit: false,
    approvalDelays: false,
    topCostCenters: true,
    budgetVariance: false,
    monthlyTrends: false,
    categoryBreakdown: true,
    pendingApprovals: false,
  },
  user: {
    totalExpense: true,
    costPerUnit: false,
    approvalDelays: false,
    topCostCenters: false,
    budgetVariance: false,
    monthlyTrends: false,
    categoryBreakdown: false,
    pendingApprovals: false,
  },
};

/**
 * Get dashboard permissions for a user's roles
 */
export const getDashboardPermissions = (roles: UserRole[]): DashboardPermissions => {
  // Get the highest permission level from all roles
  // Filter out undefined roles (legacy roles that no longer exist)
  const allPermissions = roles
    .map(role => ROLE_PERMISSIONS[role])
    .filter(perms => perms !== undefined); // Remove undefined permissions

  // If no valid permissions found, default to user permissions
  if (allPermissions.length === 0) {
    return ROLE_PERMISSIONS.user;
  }

  return allPermissions.reduce((acc, curr) => ({
    canViewOverview: acc.canViewOverview || curr.canViewOverview,
    canViewKpis: acc.canViewKpis || curr.canViewKpis,
    canViewAiInsights: acc.canViewAiInsights || curr.canViewAiInsights,
    canViewUnitComparison: acc.canViewUnitComparison || curr.canViewUnitComparison,
    canViewAdvancedAnalytics: acc.canViewAdvancedAnalytics || curr.canViewAdvancedAnalytics,
    canExportData: acc.canExportData || curr.canExportData,
    canManageApprovals: acc.canManageApprovals || curr.canManageApprovals,
    canViewAllUnits: acc.canViewAllUnits || curr.canViewAllUnits,
    canScheduleReports: acc.canScheduleReports || curr.canScheduleReports,
    canViewFinancialDetails: acc.canViewFinancialDetails || curr.canViewFinancialDetails,
  }), ROLE_PERMISSIONS.user);
};

/**
 * Get KPI visibility for a user's roles
 */
export const getKpiVisibility = (roles: UserRole[]): KpiVisibility => {
  // Filter out undefined roles (legacy roles that no longer exist)
  const allVisibility = roles
    .map(role => ROLE_KPI_VISIBILITY[role])
    .filter(vis => vis !== undefined);

  // If no valid visibility found, default to user visibility
  if (allVisibility.length === 0) {
    return ROLE_KPI_VISIBILITY.user;
  }

  return allVisibility.reduce((acc, curr) => ({
    totalExpense: acc.totalExpense || curr.totalExpense,
    costPerUnit: acc.costPerUnit || curr.costPerUnit,
    approvalDelays: acc.approvalDelays || curr.approvalDelays,
    topCostCenters: acc.topCostCenters || curr.topCostCenters,
    budgetVariance: acc.budgetVariance || curr.budgetVariance,
    monthlyTrends: acc.monthlyTrends || curr.monthlyTrends,
    categoryBreakdown: acc.categoryBreakdown || curr.categoryBreakdown,
    pendingApprovals: acc.pendingApprovals || curr.pendingApprovals,
  }), ROLE_KPI_VISIBILITY.user);
};

/**
 * Check if user has any of the specified roles
 */
export const hasAnyRole = (userRoles: UserRole[], requiredRoles: UserRole[]): boolean => {
  return requiredRoles.some(role => userRoles.includes(role));
};

/**
 * Check if user has all of the specified roles
 */
export const hasAllRoles = (userRoles: UserRole[], requiredRoles: UserRole[]): boolean => {
  return requiredRoles.every(role => userRoles.includes(role));
};

/**
 * Get the highest role level for prioritization
 */
export const getHighestRole = (roles: UserRole[]): UserRole => {
  const rolePriority: Record<UserRole, number> = {
    superadmin: 6,
    admin: 5,
    manager: 4,
    auditor: 3,
    editor: 2,
    user: 1,
  };

  // Filter to only valid roles
  const validRoles = roles.filter(role => rolePriority[role] !== undefined);

  // If no valid roles, default to user
  if (validRoles.length === 0) {
    return 'user';
  }

  return validRoles.reduce((highest, current) =>
    rolePriority[current] > rolePriority[highest] ? current : highest
  , 'user');
};
